import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { apiClient } from '@/api/client'
import type { StatisticsResponse } from '@/features/statistics/types'
import type { DeepOrder } from '@/features/orders/types'
import type { DashboardStats, RecentActivity } from '../types'

interface UseDashboardDataParams {
  restaurantId: number
}

function getTodayDateString(): string {
  const today = new Date()
  return today.toISOString().split('T')[0]
}

export function useDashboardData({ restaurantId }: UseDashboardDataParams) {
  const today = getTodayDateString()

  // Fetch today's statistics
  const statsQuery = useQuery({
    queryKey: ['dashboard-stats', restaurantId, today],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('restaurantId', String(restaurantId))
      params.append('startDate', today)
      params.append('endDate', today)
      params.append('timePeriod', 'daily')

      const response = await apiClient.get<StatisticsResponse>(
        `/aggregatedStatisticsApi?${params.toString()}`
      )
      return response.data
    },
    enabled: !!restaurantId,
    staleTime: 30000, // Consider fresh for 30 seconds
  })

  // Fetch today's orders for pending count and recent activity
  const ordersQuery = useQuery({
    queryKey: ['dashboard-orders', restaurantId, today],
    queryFn: async () => {
      const response = await apiClient.get<DeepOrder[]>(
        `/api/admin/restaurants/${restaurantId}/orders?date=${today}`
      )
      return response.data
    },
    enabled: !!restaurantId,
    staleTime: 15000, // Consider fresh for 15 seconds
  })

  // Compute dashboard stats from the fetched data
  const dashboardStats: DashboardStats = useMemo(() => {
    const summary = statsQuery.data?.summaryStats
    const orders = ordersQuery.data ?? []

    // Count pending orders (orders that need attention)
    const pendingOrdersCount = orders.filter(
      (order) =>
        order.confirmationStatus === 'pending' && order.cancelStatus === 'not_cancelled'
    ).length

    return {
      todayOrderCount: summary?.totalOrders ?? 0,
      todayRevenue: summary?.totalRevenue ?? 0,
      pendingOrdersCount,
      averageOrderValue: summary?.averageOrderValue ?? 0,
    }
  }, [statsQuery.data, ordersQuery.data])

  // Transform orders into recent activity feed
  const recentActivity: RecentActivity[] = useMemo(() => {
    const orders = ordersQuery.data ?? []

    // Sort by date descending and take last 10
    const sortedOrders = [...orders]
      .sort((a, b) => b.date - a.date)
      .slice(0, 10)

    return sortedOrders.map((order) => {
      let type: RecentActivity['type'] = 'order_received'
      let description = `Order #${order.id}`

      if (order.cancelStatus === 'cancelled') {
        type = 'order_cancelled'
        description = `Order #${order.id} cancelled`
      } else if (order.status === 'completed') {
        type = 'order_completed'
        description = `Order #${order.id} completed`
      } else if (order.confirmationStatus === 'confirmed') {
        type = 'status_change'
        description = `Order #${order.id} confirmed`
      }

      return {
        id: order.id,
        type,
        orderId: order.id,
        timestamp: order.date,
        description,
        amount: order.price,
      }
    })
  }, [ordersQuery.data])

  const refresh = () => {
    void statsQuery.refetch()
    void ordersQuery.refetch()
  }

  return {
    stats: dashboardStats,
    recentActivity,
    isLoading: statsQuery.isLoading || ordersQuery.isLoading,
    isError: statsQuery.isError || ordersQuery.isError,
    error: statsQuery.error ?? ordersQuery.error,
    refresh,
  }
}
